/**
 * 食事追加/編集 統一ボトムシート
 *
 * meal が渡されれば編集モード、渡されなければ追加モード。
 * 見出し: 種別・時刻（既存の食事に追加になるときはそう表示）と、タブ（検索 / AI / お気に入り / 履歴 / DB）
 * 中身: 選んだタブ。検索はパレットで、一覧はシート全体で 1 つのスクロール
 * 下の帯: 選んだ食品・記録後の残り kcal・記録ボタン（キーボードの上に固定）
 * 追加のみ: お気に入り登録オプション
 * 編集のみ: 既存アイテム一覧（編集・削除）、食事削除ボタン
 */

import { Pencil, RotateCcw, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  analyzeMeal,
  analyzeMealWithImage,
  createMeal,
  createTemplate,
  createTemplateWithImage,
  deleteMeal,
  listTemplates,
  regenerateMeal,
  updateMeal,
} from '@/api/services/nutrition'
import type {
  FoodTemplate,
  ItemUpdate,
  MealItem,
  MealItemHistoryEntry,
  MealItemInput,
  MealLog,
  MealType,
} from '@/api/types/nutrition'
import { PhotoInput } from '@/components/shared/photo-input'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { confirmDialog } from '@/lib/confirm-dialog'

import { CartTray } from './cart-tray'
import {
  buildUnifiedCart,
  cartToItems,
  dbCartKey,
  type FoodCartItem,
  historyCartKey,
  type PalettePick,
  submitLabel,
  toggleByKey,
  type UnifiedCartItem,
} from './cart-utils'
import { FoodPalette } from './food-palette/food-palette'
import type { SheetInit, SheetTab } from './food-palette/palette-utils'
import { SheetTabs } from './food-palette/sheet-tabs'
import { FoodSearchSection } from './food-search-section'
import { SHEET_LIST_MAX_HEIGHT } from './list-heights'
import { MealHistorySection } from './meal-history-section'
import { MealTypeSelect } from './meal-type-select'
import { MEAL_TYPE_LABELS } from './meal-types'
import { addItemsToMeal, parseAiItems } from './quick-add'
import { TemplateCreateDialog } from './template-create-dialog'
import { TemplateEditDialog } from './template-edit-dialog'
import { TemplateFavoritesSection } from './template-favorites-section'
import { currentTime, getNextMealType, inputToTime, scaleQuantity, timeToInput } from './utils'

// ==============================================================================
// Types
// ==============================================================================

type LocalItem = {
  id: number
  name: string
  quantity: string
  calories: number
  protein_g: number
  fat_g: number
  carb_g: number
  scale: number
}

function toLocalItem(item: MealItem): LocalItem {
  return {
    id: item.id,
    name: item.name,
    quantity: item.quantity ?? '',
    calories: item.calories,
    protein_g: item.protein_g,
    fat_g: item.fat_g,
    carb_g: item.carb_g,
    scale: item.scale ?? 1,
  }
}

type MealSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 追加モード用: 日付 */
  date?: string
  /** 追加モード用: 当日の食事一覧（次の食事タイプ推定に使用） */
  meals?: MealLog[]
  /** 追加モード用: テンプレート一覧 */
  templates?: FoodTemplate[]
  /** 編集モード用: 編集対象の食事 */
  meal?: MealLog | null
  onSaved: () => void
  onTemplateCreated?: () => void
  /** 目標 − 今日の合計 kcal。目標が無いときは null（下の帯に残りを出さない） */
  remainingCalories?: number | null
  /** 追加モード用: 開いたときのタブ・AI 入力欄・DB の検索文字（記録バーから開いたとき） */
  initial?: SheetInit | null
}

const EMPTY_MEALS: MealLog[] = []

export function MealSheet({
  open,
  onOpenChange,
  date,
  meals = EMPTY_MEALS,
  templates: externalTemplates,
  meal,
  onSaved,
  onTemplateCreated,
  remainingCalories = null,
  initial = null,
}: MealSheetProps) {
  const isEdit = !!meal

  // --- 共通 state ---
  const [mealType, setMealType] = useState<MealType>('breakfast')
  const [eatenAt, setEatenAt] = useState('')
  const { isSubmitting: submitting, run } = useSubmitLock()
  const [tab, setTab] = useState<SheetTab>('search')
  // DB タブで最初に検索する文字（検索タブの「DB で〇〇を検索」から渡す）
  const [dbQuery, setDbQuery] = useState('')
  const [editingTemplate, setEditingTemplate] = useState<FoodTemplate | null>(null)
  const [showCreateTemplate, setShowCreateTemplate] = useState(false)

  // 追加モードで既存食事がある場合（snack以外）→ その食事の編集に切り替え
  const existingMeal = useMemo(() => {
    if (isEdit || !meals.length) return null
    return meals.find((m) => m.meal_type === mealType && mealType !== 'snack') ?? null
  }, [isEdit, meals, mealType])

  const effectiveMeal = existingMeal ?? meal
  const effectiveIsEdit = !!effectiveMeal

  // テンプレート選択
  const [internalTemplates, setInternalTemplates] = useState<FoodTemplate[]>([])
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<number>>(new Set())
  const [templateMultipliers, setTemplateMultipliers] = useState<Map<number, string>>(new Map())

  // AI入力
  const [memo, setMemo] = useState('')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])

  // DB検索
  const [foodCart, setFoodCart] = useState<FoodCartItem[]>([])
  // 履歴
  const [historyCart, setHistoryCart] = useState<MealItemHistoryEntry[]>([])
  // 履歴・DB の行ごとの倍率（お気に入りは templateMultipliers）
  const [itemMultipliers, setItemMultipliers] = useState<Map<string, number>>(new Map())

  // 追加モード: お気に入り登録
  const [saveAsTemplate, setSaveAsTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')

  // 編集モード: 既存アイテム管理
  const [localItems, setLocalItems] = useState<LocalItem[]>([])
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [modifiedIds, setModifiedIds] = useState<Set<number>>(new Set())
  const [editingId, setEditingId] = useState<number | null>(null)
  const [addedItems, setAddedItems] = useState<MealItemInput[]>([])

  const templates = externalTemplates ?? internalTemplates

  const fetchTemplates = useCallback(() => {
    listTemplates().then(setInternalTemplates)
  }, [])

  // リセット
  useEffect(() => {
    if (!open) return
    if (meal) {
      setMealType(meal.meal_type)
      setEatenAt(timeToInput(meal.eaten_at))
      setLocalItems(meal.items.map(toLocalItem))
      setRemovedIds(new Set())
      setModifiedIds(new Set())
      setEditingId(null)
      setAddedItems([])
      if (!externalTemplates) fetchTemplates()
    } else {
      setMealType(getNextMealType(meals))
      setEatenAt(currentTime())
      setLocalItems([])
      setRemovedIds(new Set())
      setModifiedIds(new Set())
      setEditingId(null)
      setAddedItems([])
      setSaveAsTemplate(false)
      setTemplateName('')
    }
    const preset = meal ? null : initial
    setTab(preset?.tab ?? 'search')
    setMemo(preset?.memo ?? '')
    setDbQuery(preset?.dbQuery ?? '')
    setImageFiles([])
    setImagePreviews([])
    setSelectedTemplateIds(new Set())
    setTemplateMultipliers(new Map())
    setFoodCart([])
    setHistoryCart([])
    setItemMultipliers(new Map())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meal])

  // 追加モードでmealType変更時: 既存食事があればアイテムをロード
  useEffect(() => {
    if (!open || isEdit) return
    if (existingMeal) {
      setLocalItems(existingMeal.items.map(toLocalItem))
      setEatenAt(timeToInput(existingMeal.eaten_at))
      setRemovedIds(new Set())
      setModifiedIds(new Set())
      setEditingId(null)
      setAddedItems([])
    } else {
      setLocalItems([])
      setEatenAt(currentTime())
      setRemovedIds(new Set())
      setModifiedIds(new Set())
      setEditingId(null)
      setAddedItems([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMeal?.id])

  const toggleTemplate = useCallback((id: number) => {
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // 統一カート
  const unifiedCart = useMemo<UnifiedCartItem[]>(
    () =>
      buildUnifiedCart({
        templates,
        selectedTemplateIds,
        templateMultipliers,
        foodCart,
        historyCart,
        itemMultipliers,
      }),
    [selectedTemplateIds, templates, templateMultipliers, foodCart, historyCart, itemMultipliers],
  )

  const handleRemoveCartItem = useCallback((item: UnifiedCartItem) => {
    if (item.source === 'template' && item.templateId != null) {
      const { templateId } = item
      setSelectedTemplateIds((prev) => {
        const next = new Set(prev)
        next.delete(templateId)
        return next
      })
    } else if (item.source === 'history') {
      setHistoryCart((prev) => prev.filter((e) => historyCartKey(e) !== item.key))
    } else {
      setFoodCart((prev) => prev.filter((f) => dbCartKey(f) !== item.key))
    }
  }, [])

  const handleCartMultiplier = useCallback((item: UnifiedCartItem, multiplier: number) => {
    if (item.source === 'template' && item.templateId != null) {
      const { templateId } = item
      setTemplateMultipliers((prev) => new Map(prev).set(templateId, String(multiplier)))
      return
    }
    setItemMultipliers((prev) => new Map(prev).set(item.key, multiplier))
  }, [])

  // パレットで選ぶとカートに出し入れする
  const isPicked = (pick: PalettePick) => {
    if (pick.kind === 'template') return selectedTemplateIds.has(pick.template.id)
    return historyCart.some((e) => e.item_id === pick.entry.item_id)
  }

  const handlePick = (pick: PalettePick) => {
    if (pick.kind === 'template') toggleTemplate(pick.template.id)
    else setHistoryCart((prev) => toggleByKey(prev, pick.entry, historyCartKey))
  }

  // --- 編集モード: アイテム操作 ---
  const visibleItems = localItems.filter((i) => !removedIds.has(i.id))

  const handleRemoveItem = (itemId: number) => {
    setRemovedIds(new Set([...removedIds, itemId]))
    if (editingId === itemId) setEditingId(null)
  }

  const handleResetItem = (itemId: number) => {
    if (!effectiveMeal) return
    const original = effectiveMeal.items.find((i) => i.id === itemId)
    if (!original) return
    setLocalItems((prev) => prev.map((i) => (i.id === itemId ? toLocalItem(original) : i)))
    const next = new Set(modifiedIds)
    next.delete(itemId)
    setModifiedIds(next)
  }

  const handleEditConfirm = (item: LocalItem) => {
    setLocalItems((prev) => prev.map((i) => (i.id === item.id ? item : i)))
    setModifiedIds(new Set([...modifiedIds, item.id]))
    setEditingId(null)
  }

  // --- 送信判定 ---
  const hasMemo = memo.trim().length > 0
  const hasImage = imageFiles.length > 0
  const hasAi = hasMemo || hasImage
  const hasCart = unifiedCart.length > 0

  // 追加モード
  const canSubmitAdd = hasMemo || hasCart || hasImage

  // 編集モード
  const mealTypeChanged = effectiveIsEdit && effectiveMeal ? mealType !== effectiveMeal.meal_type : false
  const timeChanged = effectiveIsEdit && effectiveMeal ? eatenAt !== timeToInput(effectiveMeal.eaten_at) : false
  const hasItemChanges = removedIds.size > 0 || modifiedIds.size > 0 || addedItems.length > 0 || hasCart
  const canSubmitEdit = hasItemChanges || mealTypeChanged || timeChanged || hasMemo || hasImage

  // --- 送信 ---
  const handleSubmitAdd = async () => {
    if (!canSubmitAdd || !date) return
    await run(async () => {
      try {
        const eatenAtValue = inputToTime(eatenAt)
        const memoTrimmed = memo.trim()

        const cartItems = cartToItems(unifiedCart)
        const target = await createMeal({
          date,
          meal_type: mealType,
          memo: memoTrimmed || null,
          eaten_at: eatenAtValue,
        })
        // 間食以外で同じ日・同じ種別の食事があると、新しく作らずにそれが返る
        const isExisting = target.items.length > 0

        if (!isExisting && hasImage) {
          // 画像を食事に保存して AI 解析（食品は解析結果で上書きされる）。カートはそのあとに追記して倍率を保つ
          await analyzeMealWithImage(target.id, imageFiles)
          await addItemsToMeal(target.id, cartItems, eatenAtValue)
        } else if (!isExisting && hasMemo && !hasCart) {
          // メモを食事に残したまま AI 解析（あとで再解析するときの手がかりになる）
          await analyzeMeal(target.id)
        } else {
          const aiItems = hasAi ? await parseAiItems(memoTrimmed, imageFiles[0]) : []
          await addItemsToMeal(target.id, [...cartItems, ...aiItems], eatenAtValue)
        }

        if (saveAsTemplate && (hasMemo || hasImage)) {
          if (hasImage) {
            await createTemplateWithImage(imageFiles[0], memo.trim(), templateName.trim() || null)
          } else {
            await createTemplate({ text: memo.trim(), name: templateName.trim() || null })
          }
          onTemplateCreated?.()
        }

        const parts: string[] = []
        if (hasCart) parts.push(`食品${unifiedCart.length}件`)
        if (hasMemo || hasImage) parts.push('AI解析')
        if (saveAsTemplate) parts.push('お気に入り登録')
        toast.success(`${parts.join(' + ')} を追加しました`)
        onOpenChange(false)
        onSaved()
      } catch {
        toast.error('記録に失敗しました')
      }
    })
  }

  const handleSubmitEdit = async () => {
    if (!effectiveMeal) return
    await run(async () => {
      try {
        if (mealTypeChanged || timeChanged) {
          await updateMeal(effectiveMeal.id, { eaten_at: inputToTime(eatenAt), meal_type: mealType })
        }

        // AI入力がある場合 → 追加モード（既存アイテムを維持）
        const aiItems = hasAi ? await parseAiItems(memo.trim(), imageFiles[0]) : []

        // アイテムの追加/削除/更新
        const allNewItems: MealItemInput[] = [...addedItems, ...cartToItems(unifiedCart), ...aiItems]
        const hasModifications = removedIds.size > 0 || modifiedIds.size > 0 || allNewItems.length > 0
        if (hasModifications) {
          const updateItems: ItemUpdate[] = localItems
            .filter((i) => modifiedIds.has(i.id) && !removedIds.has(i.id))
            .map((i) => ({
              id: i.id,
              name: i.name,
              quantity: i.quantity || null,
              calories: i.calories,
              protein_g: i.protein_g,
              fat_g: i.fat_g,
              carb_g: i.carb_g,
              scale: i.scale,
            }))
          await regenerateMeal(effectiveMeal.id, {
            remove_item_ids: removedIds.size > 0 ? [...removedIds] : undefined,
            update_items: updateItems.length > 0 ? updateItems : undefined,
            add_items: allNewItems.length > 0 ? allNewItems : undefined,
          })
        }

        toast.success('保存しました')
        onOpenChange(false)
        onSaved()
      } catch {
        toast.error('保存に失敗しました')
      }
    })
  }

  const handleDelete = async () => {
    if (!effectiveMeal) return
    if (!(await confirmDialog({ title: 'この食事を削除しますか？', destructive: true }))) return
    await run(async () => {
      try {
        await deleteMeal(effectiveMeal.id)
        toast.success('削除しました')
        onOpenChange(false)
        onSaved()
      } catch {
        toast.error('削除に失敗しました')
      }
    })
  }

  const handleSubmit = effectiveIsEdit ? handleSubmitEdit : handleSubmitAdd
  const canSubmit = effectiveIsEdit ? canSubmitEdit : canSubmitAdd

  const mealLabel = MEAL_TYPE_LABELS[mealType]
  const sheetTitle = isEdit
    ? '食事を編集'
    : existingMeal
      ? `${mealLabel}に追加（今 ${existingMeal.items.length}品）`
      : '食事を追加'
  // 既存の食事に追加するシートで、食品の修正しかしていないときは「保存」
  const onlyEditingExisting = !!existingMeal && !hasCart && !hasAi
  const label = submitLabel({
    isEdit: isEdit || onlyEditingExisting,
    mealLabel,
    cartCount: unifiedCart.length,
    hasAi,
  })

  return (
    <>
      <BottomSheet
        title={sheetTitle}
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={[0.92]}
        headerContent={
          <div className="space-y-2 border-b border-border px-4 py-2">
            <div className="flex items-center gap-2">
              <MealTypeSelect
                value={mealType}
                onValueChange={setMealType}
                triggerClassName="h-8 w-24 text-xs"
                itemClassName="text-xs"
              />
              <Input
                type="time"
                value={eatenAt}
                onChange={(e) => setEatenAt(e.target.value)}
                className="h-8 w-24 text-xs"
                aria-label="食べた時刻"
              />
              <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{sheetTitle}</h2>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => onOpenChange(false)}
                aria-label="閉じる"
              >
                <X className="size-5" />
              </Button>
            </div>
            <SheetTabs value={tab} onChange={setTab} />
          </div>
        }
        footerAboveKeyboard
        footer={
          <CartTray
            items={unifiedCart}
            onMultiplierChange={handleCartMultiplier}
            onRemove={handleRemoveCartItem}
            remainingCalories={remainingCalories}
            label={label}
            onSubmit={handleSubmit}
            canSubmit={canSubmit}
            submitting={submitting}
            onDelete={isEdit ? handleDelete : undefined}
          />
        }
      >
        <div className="space-y-4 px-4 pt-2 pb-4">
          {/* 編集モード: 既存アイテム一覧 */}
          {effectiveIsEdit && visibleItems.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">食品一覧</label>
              <div className="divide-y rounded-md border">
                {visibleItems.map((item) =>
                  editingId === item.id ? (
                    <EditItemRow
                      key={item.id}
                      item={item}
                      onConfirm={handleEditConfirm}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <div
                      key={item.id}
                      className={`flex items-center gap-1 px-2 py-1.5 ${modifiedIds.has(item.id) ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium">
                          {item.name}
                          {(() => {
                            const q = scaleQuantity(item.quantity, item.scale ?? 1)
                            return q && <span className="ml-1 text-muted-foreground">{q}</span>
                          })()}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {item.calories}kcal P{item.protein_g} F{item.fat_g} C{item.carb_g}
                        </p>
                      </div>
                      {modifiedIds.has(item.id) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => handleResetItem(item.id)}
                          title="元に戻す"
                        >
                          <RotateCcw className="size-3" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-muted-foreground"
                        onClick={() => setEditingId(item.id)}
                        title="編集"
                      >
                        <Pencil className="size-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={() => handleRemoveItem(item.id)}
                        title="削除"
                      >
                        <X className="size-3" />
                      </Button>
                    </div>
                  ),
                )}
                {/* 編集モード: AI/DB由来の追加予定アイテム */}
                {addedItems.map((item, idx) => (
                  <div
                    key={`new-${idx}`}
                    className="flex items-center gap-1 bg-green-50 px-2 py-1.5 dark:bg-green-950/30"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">
                        {item.name}
                        {(() => {
                          const q = scaleQuantity(item.quantity, item.scale ?? 1)
                          return q && <span className="ml-1 text-muted-foreground">{q}</span>
                        })()}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {item.calories}kcal P{item.protein_g} F{item.fat_g} C{item.carb_g}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-destructive"
                      onClick={() => setAddedItems((prev) => prev.filter((_, i) => i !== idx))}
                      title="削除"
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* タブ: 検索（パレット） */}
          {tab === 'search' && (
            <FoodPalette
              templates={templates}
              isSelected={isPicked}
              onPick={handlePick}
              onEditTemplate={setEditingTemplate}
              onAiText={(text) => {
                setMemo((prev) => (prev.trim() ? `${prev.trim()}、${text}` : text))
                setTab('ai')
              }}
              onSearchDb={(query) => {
                setDbQuery(query)
                setTab('db')
              }}
              stickyInput
              listClassName="max-h-none overflow-visible"
              autoFocus={!isEdit}
            />
          )}

          {/* タブ: AI（写真・テキストを保存時に解析） */}
          {tab === 'ai' && (
            <div className="space-y-3">
              <div className="space-y-2 rounded-md border p-2">
                <PhotoInput
                  label="食事内容"
                  files={imageFiles}
                  previews={imagePreviews}
                  onFilesChange={(files, previews) => {
                    setImageFiles(files)
                    setImagePreviews(previews)
                  }}
                  maxFiles={5}
                />
                <Textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="食事内容を入力（例: 松屋の牛めし並盛、味噌汁）"
                  rows={3}
                />
              </div>
              {/* お気に入り登録（追加モードのみ） */}
              {!effectiveIsEdit && (hasMemo || hasImage) && (
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-center gap-2">
                    <Checkbox checked={saveAsTemplate} onCheckedChange={(c) => setSaveAsTemplate(c === true)} />
                    <span className="text-sm">お気に入りにも登録</span>
                  </label>
                  {saveAsTemplate && (
                    <Input
                      placeholder="テンプレート名（省略可）"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* タブ: お気に入り・履歴・DB の一覧 */}
          {tab === 'favorites' && (
            <div className="rounded-md border p-2">
              <TemplateFavoritesSection
                templates={externalTemplates}
                selectedIds={selectedTemplateIds}
                onToggle={toggleTemplate}
                multipliers={templateMultipliers}
                onMultiplierChange={(id, value) => {
                  setTemplateMultipliers((prev) => {
                    const next = new Map(prev)
                    next.set(id, value)
                    return next
                  })
                }}
                onEditTemplate={setEditingTemplate}
                onCreateTemplate={() => setShowCreateTemplate(true)}
                onTemplateChanged={() => {
                  onTemplateCreated?.()
                  if (!externalTemplates) fetchTemplates()
                }}
                listMaxHeight={SHEET_LIST_MAX_HEIGHT}
              />
            </div>
          )}
          {tab === 'history' && (
            <div className="rounded-md border p-2">
              <MealHistorySection
                selected={historyCart}
                onSelectedChange={setHistoryCart}
                listMaxHeight={SHEET_LIST_MAX_HEIGHT}
              />
            </div>
          )}
          {tab === 'db' && (
            <div className="rounded-md border p-2">
              <FoodSearchSection
                cart={foodCart}
                onCartChange={setFoodCart}
                listMaxHeight={SHEET_LIST_MAX_HEIGHT}
                hideHeading
                initialQuery={dbQuery}
              />
            </div>
          )}
        </div>
      </BottomSheet>

      {/* テンプレート編集ダイアログ */}
      <TemplateEditDialog
        template={editingTemplate}
        open={editingTemplate !== null}
        onOpenChange={(o) => {
          if (!o) setEditingTemplate(null)
        }}
        onSaved={() => {
          setEditingTemplate(null)
          onTemplateCreated?.()
          if (!externalTemplates) fetchTemplates()
        }}
      />

      {/* テンプレート新規追加ダイアログ */}
      <TemplateCreateDialog
        open={showCreateTemplate}
        onOpenChange={setShowCreateTemplate}
        onCreated={() => {
          onTemplateCreated?.()
          if (!externalTemplates) fetchTemplates()
        }}
      />
    </>
  )
}

// ==============================================================================
// EditItemRow
// ==============================================================================

function EditItemRow({
  item,
  onConfirm,
  onCancel,
}: {
  item: LocalItem
  onConfirm: (item: LocalItem) => void
  onCancel: () => void
}) {
  // 編集開始時の base 値（scale を取り除いた 1倍時点の値）を保持。
  // 倍率変更時は常にこの base から再計算する。
  const initialScale = item.scale > 0 ? item.scale : 1
  const baseRef = useRef({
    calories: item.calories / initialScale,
    protein_g: item.protein_g / initialScale,
    fat_g: item.fat_g / initialScale,
    carb_g: item.carb_g / initialScale,
  })
  const [form, setForm] = useState(item)
  const [multiplier, setMultiplier] = useState(initialScale.toString())

  const handleMultiplierChange = (raw: string) => {
    setMultiplier(raw)
    const mult = parseFloat(raw)
    if (!Number.isFinite(mult) || mult <= 0) return
    const base = baseRef.current
    setForm((prev) => ({
      ...prev,
      calories: Math.round(base.calories * mult),
      protein_g: Math.round(base.protein_g * mult * 10) / 10,
      fat_g: Math.round(base.fat_g * mult * 10) / 10,
      carb_g: Math.round(base.carb_g * mult * 10) / 10,
    }))
  }

  const handleConfirm = () => {
    const mult = parseFloat(multiplier)
    if (!Number.isFinite(mult) || mult <= 0) {
      // multiplier が不正な状態で確定すると、form.calories/P/F/C は
      // 前回の倍率で計算済みの値のまま scale=1 として保存され不整合になるため拒否する。
      toast.error('倍率には 0 より大きい数値を入力してください')
      return
    }
    onConfirm({ ...form, scale: mult })
  }

  return (
    <div className="space-y-2 bg-muted/50 p-2">
      <div className="grid grid-cols-2 gap-1.5">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="名前"
          className="h-7 text-xs"
        />
        <Input
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          placeholder="量"
          className="h-7 text-xs"
        />
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">kcal</label>
          <Input
            value={form.calories}
            onChange={(e) => setForm({ ...form, calories: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">P</label>
          <Input
            value={form.protein_g}
            onChange={(e) => setForm({ ...form, protein_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">F</label>
          <Input
            value={form.fat_g}
            onChange={(e) => setForm({ ...form, fat_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">C</label>
          <Input
            value={form.carb_g}
            onChange={(e) => setForm({ ...form, carb_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <label className="text-xs text-muted-foreground">倍率:</label>
        <Input
          value={multiplier}
          onChange={(e) => handleMultiplierChange(e.target.value)}
          className="h-7 w-16 text-xs"
          type="number"
          step="0.1"
          min="0.1"
        />
        <div className="ml-auto flex gap-1">
          <Button size="sm" className="h-6 px-2 text-xs" onClick={handleConfirm}>
            確定
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={onCancel}>
            取消
          </Button>
        </div>
      </div>
    </div>
  )
}
